import { Box, Button, Link, Text, VStack } from '@chakra-ui/react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { formatFeScheduleWhen } from '@/components/forms/parentTicket/feDispatchUtils'
import type { TaskFieldModuleProps } from '../types'
import type { FESchedule } from '@/types/parentTicket'
import AssetsPanel from '../../AssetsPanel'
import TicketStorePicker from '../../TicketStorePicker'
import WorkPermitPanel, { uploadTaskWorkPermitAttachment } from '../../WorkPermitPanel'
import PartProcurementPanel from '../../PartProcurementPanel'
import TaskModuleSharedBaseFields from '../../TaskModuleSharedBaseFields'
import TicketProperties from '@/components/helpdesk/TicketDetail/TicketProperties'
import PartsInfoSection from '@/components/helpdesk/TicketDetail/PartsInfoSection'
import type { ComponentProps } from 'react'

type TicketPropertiesProps = ComponentProps<typeof TicketProperties>

export function StoreModule({ field, parentTicketId, ticketPropertiesProps, isReadOnly, onRefresh }: TaskFieldModuleProps) {
  const storeId = ticketPropertiesProps?.storeId as number | null | undefined
  const clientCode = ticketPropertiesProps?.clientCode as string | undefined
  const onStoreUpdated = ticketPropertiesProps?.onStoreUpdated as (() => void | Promise<void>) | undefined

  if (storeId) {
    return (
      <Box mb={3}>
        <Text fontSize="xs" fontWeight="700" color="gray.500" mb={1}>
          {field.label}
        </Text>
        <Text fontSize="sm">Store linked (code: {String(ticketPropertiesProps?.storeCode ?? storeId)})</Text>
      </Box>
    )
  }

  return (
    <Box mb={3}>
      <TicketStorePicker
        ticketId={parentTicketId}
        clientCode={clientCode}
        isReadOnly={isReadOnly}
        onSaved={async () => {
          await onStoreUpdated?.()
          onRefresh?.()
        }}
      />
    </Box>
  )
}

export function FeScheduleModule({ field, parentTicketId, ticketPropertiesProps, task }: TaskFieldModuleProps) {
  const ticketId = (ticketPropertiesProps?.ticketId as string | undefined) ?? parentTicketId

  return (
    <Box mb={3}>
      <Text fontSize="xs" fontWeight="700" color="gray.500" mb={2} textTransform="uppercase">
        {field.label}
      </Text>
      <TicketProperties
        {...(ticketPropertiesProps as TicketPropertiesProps)}
        ticketId={ticketId}
        contentMode="details"
        taskSection="fe"
        childTicketId={task?.id}
        embedded
      />
    </Box>
  )
}

export function FeScheduleReferenceModule({ field, ticketPropertiesProps, task }: TaskFieldModuleProps) {
  const colors = useThemeColors()
  const childTicketId = task?.id
  const allSchedules = (ticketPropertiesProps?.feSchedules as FESchedule[] | undefined) ?? []
  const linked = childTicketId != null
    ? allSchedules.filter((s) => s.child_ticket_id === childTicketId)
    : []
  const schedules = linked

  if (schedules.length === 0) {
    return (
      <Box mb={3}>
        <Text fontSize="xs" fontWeight="700" color="gray.500" mb={1} textTransform="uppercase">
          {field.label}
        </Text>
        <Text fontSize="sm" color={colors.textSecondary}>
          No FE schedule linked to this ticket yet.
        </Text>
      </Box>
    )
  }

  return (
    <Box mb={3}>
      <Text fontSize="xs" fontWeight="700" color="gray.500" mb={2} textTransform="uppercase">
        {field.label}
      </Text>
      <VStack align="stretch" spacing={2}>
        {schedules.map((schedule) => (
          <Box
            key={schedule.id}
            p={3}
            border="1px solid"
            borderColor={colors.borderLight}
            borderRadius="md"
            bg={colors.inputBg ?? 'gray.50'}
          >
            <Text fontSize="sm" fontWeight="600">
              {schedule.field_engineer?.name ?? `FE #${schedule.fe_user_id}`}
              <Text as="span" fontWeight="normal" color={colors.textSecondary}>
                {' '}
                · {schedule.role}
              </Text>
            </Text>
            <Text fontSize="sm" color={colors.textSecondary}>
              {formatFeScheduleWhen(schedule)}
            </Text>
            {schedule.location_address ? (
              <Text fontSize="xs" color={colors.textSecondary} mt={1}>
                {schedule.location_address}
              </Text>
            ) : null}
            {schedule.external_dispatch_status ? (
              <Text fontSize="xs" color={colors.textSecondary} mt={1}>
                Dispatch status: {schedule.external_dispatch_status}
              </Text>
            ) : null}
          </Box>
        ))}
      </VStack>
    </Box>
  )
}

export function AssetsModule({ field, parentTicketId, ticketPropertiesProps, isReadOnly, onRefresh }: TaskFieldModuleProps) {
  const ticket = ticketPropertiesProps?.ticket as { assets?: unknown[] } | undefined
  const onAssetsUpdated = ticketPropertiesProps?.onAssetsUpdated as (() => void | Promise<void>) | undefined

  return (
    <AssetsPanel
      ticketId={parentTicketId}
      clientCode={ticketPropertiesProps?.clientCode as string | undefined}
      storeId={ticketPropertiesProps?.storeId as number | null | undefined}
      storeName={(ticketPropertiesProps?.storeDetails as { storeName?: string | null })?.storeName ?? null}
      initialAssets={(ticket?.assets as Parameters<typeof AssetsPanel>[0]['initialAssets']) ?? []}
      isReadOnly={isReadOnly}
      onSaved={async () => {
        await onAssetsUpdated?.()
        onRefresh?.()
      }}
    />
  )
}

export function PartsModule({ field, parentTicketId, ticketPropertiesProps, isReadOnly, onRefresh }: TaskFieldModuleProps) {
  const onPartsChange = ticketPropertiesProps?.onPartsChange as (() => void) | undefined
  const onOpenProcurementTask = ticketPropertiesProps?.onOpenProcurementTask as ((taskId: number) => void) | undefined

  return (
    <Box mb={3}>
      <PartsInfoSection
        ticketId={parentTicketId}
        isReadOnly={isReadOnly ?? false}
        onOpenProcurementTask={onOpenProcurementTask}
        onPartsChange={() => {
          onPartsChange?.()
          onRefresh?.()
        }}
      />
    </Box>
  )
}

export function WorkPermitModule({ parentTicketId, ticketPropertiesProps, isReadOnly, task }: TaskFieldModuleProps) {
  const ticket = ticketPropertiesProps?.ticket as { attachments?: import('@/types/ticket').ParentTicketAttachment[] } | undefined
  const refreshUpload = ticketPropertiesProps?.onWorkPermitUpload as
    | ((ticketId: string, file: File) => Promise<void>)
    | undefined
  const childTicketId = task?.id

  const handleUpload = async (ticketId: string, file: File) => {
    await uploadTaskWorkPermitAttachment(ticketId, file, childTicketId ?? null)
    if (refreshUpload) {
      await refreshUpload(ticketId, file)
    }
  }

  return (
    <WorkPermitPanel
      ticketId={parentTicketId}
      attachments={ticket?.attachments}
      childTicketId={childTicketId}
      onUpload={handleUpload}
      isReadOnly={isReadOnly}
    />
  )
}

export function PartProcurementModule({
  field,
  parentTicketId,
  ticketPropertiesProps,
  isReadOnly,
  task,
  formData,
  onFormDataChange,
  onRefresh,
}: TaskFieldModuleProps) {
  const ticket = ticketPropertiesProps?.ticket as { ticketParts?: import('@/types/parentTicket').TicketPart[] } | undefined
  const onPartsChange = ticketPropertiesProps?.onPartsChange as (() => void) | undefined

  return (
    <Box mb={3}>
      <Text fontSize="xs" fontWeight="700" color="gray.500" mb={2} textTransform="uppercase">
        {field.label}
      </Text>
      <TaskModuleSharedBaseFields
        field={field}
        formData={formData}
        onFormDataChange={onFormDataChange}
        ticketPropertiesProps={ticketPropertiesProps}
      />
      <PartProcurementPanel
        ticketId={parentTicketId}
        childTicketId={task?.id}
        ticketParts={ticket?.ticketParts}
        isReadOnly={isReadOnly}
        onSaved={() => {
          onPartsChange?.()
          onRefresh?.()
        }}
      />
    </Box>
  )
}

export function AssigneeModulePlaceholder() {
  return null
}

export function IframeEmbedModule({ field, parentTicketId, task, formData }: TaskFieldModuleProps) {
  const colors = useThemeColors()
  const config = field.config ?? {}
  let url = String(config.url ?? '')
  url = url
    .replace(/\{\{ticket_id\}\}/g, parentTicketId)
    .replace(/\{\{task_id\}\}/g, String(task.id))
    .replace(/\{\{store_code\}\}/g, String(formData.store_code ?? ''))

  if (!url) {
    return (
      <Text fontSize="sm" color={colors.textSecondary}>
        Configure iframe URL in template step settings.
      </Text>
    )
  }

  const height = Number(config.height ?? 480)
  const sandbox = String(config.sandbox ?? 'allow-scripts allow-same-origin')

  return (
    <Box mb={3}>
      <Text fontSize="xs" fontWeight="700" mb={2}>
        {field.label}
      </Text>
      <Box as="iframe" src={url} width="100%" height={`${height}px`} border="1px solid" borderColor={colors.borderLight} borderRadius="md" sandbox={sandbox} title={field.label} />
    </Box>
  )
}

export function ExternalLinkPanelModule({ field, parentTicketId, task, formData }: TaskFieldModuleProps) {
  const config = field.config ?? {}
  const urlTemplate = String(config.urlTemplate ?? formData[field.key] ?? '')
  const buttonLabel = String(config.buttonLabel ?? 'Open external system')
  let url = urlTemplate
    .replace(/\{\{ticket_id\}\}/g, parentTicketId)
    .replace(/\{\{task_id\}\}/g, String(task.id))

  if (!url) return null

  return (
    <Box mb={3}>
      <Link href={url} isExternal>
        <Button size="sm" colorScheme="blue" variant="outline">
          {buttonLabel}
        </Button>
      </Link>
    </Box>
  )
}
